import React from 'react';
import { getPositionColorClasses } from '../utils/positionColors';

export interface WorkspaceEntry {
  name: string;
  position?: string;
  id?: string;
}

interface WorkspaceBadgeProps {
  workspace: WorkspaceEntry;
}

/**
 * Single workspace + optional position badge.
 * Canonical format: "[Workspace Name] [Position]" — workspace name first, then
 * a color-coded position mini-badge when a position is present.
 * Used on all org-level reports (bills, legislators, votes) to show which
 * workspace(s) are associated with a row and what their position is.
 */
export const WorkspaceBadge: React.FC<WorkspaceBadgeProps> = ({ workspace }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-info-100 text-info-800 dark:bg-info-900/20 dark:text-info-300 whitespace-nowrap">
    {workspace.name}
    {workspace.position && (
      <span className={`px-1 py-0.5 rounded text-xs font-semibold ${getPositionColorClasses(workspace.position)}`}>
        {workspace.position}
      </span>
    )}
  </span>
);

interface WorkspaceBadgeListProps {
  workspaces: WorkspaceEntry[];
  className?: string;
}

/**
 * Renders a flex-wrapped list of WorkspaceBadges.
 * Drop-in for any place multiple workspace+position entries need to be shown.
 */
export const WorkspaceBadgeList: React.FC<WorkspaceBadgeListProps> = ({ workspaces, className }) => {
  if (!workspaces || workspaces.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className ?? ''}`}>
      {workspaces.map((ws, i) => (
        <WorkspaceBadge key={ws.id ?? i} workspace={ws} />
      ))}
    </div>
  );
};

export default WorkspaceBadge;
